import * as React from "react";

import { useFormContext } from "react-hook-form";
import { FormattedMessage } from "react-intl";

import { Button } from "@ps-design-system/components/Button";
import { CheckboxField } from "@ps-design-system/components/Checkbox";
import { Input } from "@ps-design-system/components/Input";
import { Label } from "@ps-design-system/components/Label";
import { Text } from "@ps-design-system/components/Text";

import { useIntl } from "react-intl-compat";
import type { AmenityOption } from "~/services/env.service";
import { toSlug, toTitleCase } from "~/utils/index.utils";

import type { ListingUseType } from "../../../../../../components/src/services/listings.service";
import { USE_TYPE_SLUG } from "../../ActivateListingsPage.consts";
import { ActivateListingFormValues } from "../ActivateListingForm.schema";

type AmenitiesFieldsProps = {
  amenityOptions: AmenityOption[];
  requiredAmenityIds: string[];
  useType: ListingUseType;
};

/** The "Amenities" section: amenity checkboxes, and custom amenities. */
const AmenitiesFields = ({
  amenityOptions,
  requiredAmenityIds,
  useType,
}: AmenitiesFieldsProps) => {
  const form = useFormContext<ActivateListingFormValues>();
  const intl = useIntl();
  const slug = USE_TYPE_SLUG[useType];

  const [draftAmenity, setDraftAmenity] = React.useState("");

  const customAmenityNames = Object.keys(form.watch("customAmenities") ?? {});

  const handleAddCustomAmenity = () => {
    const trimmed = draftAmenity.trim();
    if (!trimmed) {
      return;
    }

    form.setValue(`customAmenities.${toTitleCase(trimmed)}`, true);
    setDraftAmenity("");
  };

  return (
    <div>
      <Text
        typeface="headline-2-m"
        element="p"
        className="tw-border-b tw-border-gray-300 tw-py-8 tw-mb-8"
        testId={`activate-listing-form-${slug}-amenities-header`}
      >
        <FormattedMessage
          id="hosting.activate_listings.amenities_header"
          defaultMessage="Amenities"
        />
      </Text>
      <Text typeface="body-1" element="p" className="tw-text-gray-600">
        <FormattedMessage
          id="hosting.activate_listings.amenities_text"
          defaultMessage="All amenities you select should be included in your hourly rate. If you have amenities that you charge for, do not include them here. You can add those in a later section."
        />
      </Text>
      {requiredAmenityIds.length > 0 && (
        <Text
          typeface="body-2"
          element="p"
          className="tw-text-gray-600"
          testId={`activate-listing-form-${slug}-amenities-required-text`}
        >
          <FormattedMessage
            id="hosting.activate_listings.offsite_amenities_text"
            defaultMessage="You must include WiFi, tables, and chairs to offer meetings"
          />
        </Text>
      )}

      {amenityOptions.map((amenity) => (
        <CheckboxField
          key={amenity.id}
          name={`amenities.${amenity.id}`}
          description={amenity.name}
          testId={`activate-listing-form-${slug}-amenity-${toSlug(amenity.id)}-checkbox`}
        />
      ))}
      {customAmenityNames.map((name) => (
        <CheckboxField
          key={name}
          name={`customAmenities.${name}`}
          description={name}
          testId={`activate-listing-form-${slug}-custom-amenity-${toSlug(name)}-checkbox`}
        />
      ))}
      {form.formState.errors.amenities?.root && (
        <Text
          className="tw-text-red-800"
          element="p"
          typeface="body-2"
          testId={`activate-listing-form-${slug}-amenities-error`}
        >
          {form.formState.errors.amenities.root.message}
        </Text>
      )}

      <div className="tw-flex tw-flex-col tw-gap-8 tw-mt-16">
        <Label htmlFor="custom-amenity">
          {intl.formatMessage({
            id: "hosting.activate_listings.amenities_add_custom_label",
            defaultMessage: "Add your own amenities",
          })}
        </Label>
        <div className="tw-flex tw-gap-8">
          <Input
            fullWidth
            id="custom-amenity"
            value={draftAmenity}
            placeholder={intl.formatMessage({
              id: "hosting.activate_listings.amenities_placeholder",
              defaultMessage: "Amenity name",
            })}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setDraftAmenity(e.target.value)
            }
            testId={`activate-listing-form-${slug}-custom-amenity-input`}
          />
          <Button
            onClick={handleAddCustomAmenity}
            disabled={!draftAmenity}
            testId={`activate-listing-form-${slug}-custom-amenity-add-button`}
          >
            <FormattedMessage
              id="hosting.activate_listings.add_amenity"
              defaultMessage="Add"
            />
          </Button>
        </div>
      </div>
    </div>
  );
};

export { AmenitiesFields };
